/* =========================================================================================================
   NJ PRODUCT NAVIGATION MEMORY
   - On collection pages: when a product in the main product grid is clicked, remember the list of products
     the customer was looking at (in on-screen order, including filters, sorting and "load more").
   - On product pages: if the current product is in that remembered list, point the "Previous Item" /
     "Next Item" links (sections/product-navigation.liquid) to the neighbouring products in that list.
   - If nothing is remembered, or the product is not in the list, the links rendered by Liquid are left as-is.
   Storage: sessionStorage (this browser tab only). Fails silently if storage is unavailable.
   ========================================================================================================= */
(() => {
  'use strict';

  const STORAGE_KEY = 'nj_product_nav_v1';
  const MAX_AGE_MS = 1000 * 60 * 60 * 2; // 2 hours

  /* ---------- Collection page: remember the list ---------- */

  function getMainGrid(target) {
    const section = target.closest('.shopify-section--main-collection');
    return section ? section.querySelector('product-list') : null;
  }

  function collectItems(grid) {
    const items = [];
    const seen = new Set();

    grid.querySelectorAll('product-card[handle]').forEach((card) => {
      const handle = card.getAttribute('handle');
      const link = card.querySelector('a[href*="/products/"]');
      if (!handle || !link || seen.has(handle)) return;
      seen.add(handle);
      items.push({ h: handle, u: link.getAttribute('href') });
    });

    return items;
  }

  function remember(event) {
    const link = event.target.closest && event.target.closest('a[href*="/products/"]');
    if (!link) return;

    const grid = getMainGrid(link);
    if (!grid || !grid.contains(link)) return;

    const items = collectItems(grid);
    if (items.length < 2) return;

    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ ts: Date.now(), items: items }));
    } catch (e) {}
  }

  document.addEventListener('click', remember, { capture: true });
  document.addEventListener('auxclick', remember, { capture: true }); // middle-click / open in new tab

  /* ---------- Product page: use the remembered list ---------- */

  function readState() {
    try {
      const state = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
      if (!state || !Array.isArray(state.items)) return null;
      if (!state.ts || Date.now() - state.ts > MAX_AGE_MS) return null;
      return state;
    } catch (e) {
      return null;
    }
  }

  function setLink(link, item) {
    if (!link) return;
    if (item && item.u) {
      link.setAttribute('href', item.u);
      link.hidden = false;
    } else {
      link.hidden = true;
    }
  }

  function applyNavigation() {
    const nav = document.querySelector('[data-nj-product-nav]');
    if (!nav) return;

    const state = readState();
    if (!state) return;

    const handle = nav.getAttribute('data-handle');
    const index = state.items.findIndex((item) => item.h === handle);
    if (index === -1) return; // not in the remembered list: keep the Liquid links

    const prev = index > 0 ? state.items[index - 1] : null;
    const next = index < state.items.length - 1 ? state.items[index + 1] : null;

    setLink(nav.querySelector('[data-nj-nav="prev"]'), prev);
    setLink(nav.querySelector('[data-nj-nav="next"]'), next);
    nav.hidden = !prev && !next;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', applyNavigation, { once: true });
  } else {
    applyNavigation();
  }

  // Back/forward cache: re-apply when the page is restored
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) applyNavigation();
  });
})();
